import IdleTimer from 'exacta-material-react/lib/src/components/IdleTimer';
import { TargettedSystems } from 'nots-window-renderer';
import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import ConfigControl from '../../components/ConfigControl';
import System from '../../config/SystemConfig';
import {
    DisplayConstants, IdleTimeout, MonitorConfig, NotsSystem, S80Monitors, S80Tabs
} from '../../helpers/constants';
import { jsonToCSS } from '../../helpers/styles';
import { playerVersion } from '../../helpers/utils';
import { DemoInteractionBlocker } from '../common-components/DemoInteractionBlocker';
import GuidedContainer from '../common-components/GuidedContainer';
import useLogger from '../common-components/hooks/useLogger';
import ImageContainer from '../common-components/ImageContainer';
import Map from '../common-components/Map';
import { MultimediaInteractionBlocker } from '../common-components/MultimediaBlocker';
import { PauseOverlay } from '../common-components/PauseOverlay';
import PrimaryCloseControl from '../common-components/PrimaryCloseControl';
import StartLesson from '../common-components/StartLesson';
import TracksOnImage from '../common-components/TracksOnImage';
import WindowDisplayManager from '../common-components/WindowDisplayManager';
import MultimediaDialog from '../MultimediaDialog';
import stripBackground from './background-images/side-strip.png';
import AudioInfoArea from './components/AudioInfoArea';
import AuxInfoArea from './components/AuxInfoArea';
import CommonInformationArea from './components/CommonInformationArea';
import CSRAArea from './components/CSRAArea';
import CursorInfoArea from './components/CursorInfoArea';
import PopupMenu from './components/PopupMenu';
import RoleAssignementArea from './components/RoleAssignmentArea';
import RoleInfoArea from './components/RoleInfoArea';
import SpareArea from './components/SpareArea';
import WorkModeArea from './components/WorkModeArea';
import MonitorContext from './MonitorContext';
import { colors, dimension } from './WindowConfig';

export interface IUpperMonitorProps {}

const Container = styled.div`
  ${jsonToCSS(dimension.upper)}
  background: ${colors.primary};
  color: ${colors.yellowText};
`;
const UpperStrap = styled.div`
  ${jsonToCSS(dimension.upperStrap)}
  display: flex;
`;

const StyledDiv = styled.div<any>`
  ${({ width, height, noPadding }) => `
  width: ${width};
  height: ${height};
  padding: ${noPadding ? '0px' : '3px'};
  `};
  border-right: solid 1px ${colors.border};
`;

const GeneralDisplayArea = styled.div`
  ${jsonToCSS(dimension.generalDisplayArea)}
  position: relative;
  background-color: ${colors.black};
  border-right: solid 1px ${colors.border};
`;
const Body = styled.div`
  display: flex;
`;

const RightStrip = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
`;

const CloseControlArea = styled.div`
  display: flex;
  flex-direction: row;
  ${jsonToCSS(dimension.closeControlArea)}
`;

const DataEntryArea = styled.div`
  padding: 0px;
  width: 400px;
  height: 640px;
  position: relative;
`;

const EachCloseControlArea = styled.div`
  display: flex;
  width: 50%;
  border-left: solid 1px ${colors.border};
  border-top: solid 1px ${colors.border};
  border-bottom: solid 1px ${colors.border};
  position: relative;
  > img {
    height: 20px;
    width: 20px;
    position: absolute;
    left: 50%;
    z-index: 20;
  }
  > hr {
    transform: rotate(90deg);
    height: 100px;
    position: absolute;
    top: -49px;
    left: 49px;
    opacity: 0.3;
  }
  > .rightHr {
    left: 156px;
    height: 87px;
    top: -42px;
  }
`;

const EmptyLeftAnalogStrip = styled.div`
  ${jsonToCSS(dimension.upperMonitorLeftStrip)};
  background-image: url(${stripBackground});
  display: inline-block;
`;
const EmptyRightAnalogStrip = styled.div`
  ${jsonToCSS(dimension.upperMonitorRightStrip)};
  background-image: url(${stripBackground});
  display: inline-block;
`;

const MonitorContentContainer = styled.div`
  display: inline-block;
  width: 1600px;
  height: 1200px;
  overflow: hidden;
`;

export default function UpperMonitor(props: IUpperMonitorProps) {
  const logger = useLogger('UpperMonitor.tsx', 'UpperMonitor');
  const monitorConfig = useSelector(
    (state: any) => state.executor.s80.monitorLayout
  );
  const status = useSelector(
    (state: any) => state.executor && state.executor.status
  );
  useEffect(() => {
    System.setSystem(NotsSystem.S80).setCurrentTab(S80Tabs.UPPER);
  }, []);
  const currentConfig = monitorConfig.upper;
  const { generalDisplayArea } = dimension;
  const generalDisplayAreaRef = useRef(null);
  const { t } = useTranslation();

  return (
    <Container>
      <MonitorContext.Provider
        value={{
          monitor: S80Monitors.UPPER,
        }}
      >
        <Helmet>
          <title>{`${t('appName')} - Upper Monitor ${playerVersion}`}</title>
        </Helmet>
        <StartLesson preferredDialogMonitor="lower" />
        <PauseOverlay />
        <DemoInteractionBlocker />
        <MultimediaInteractionBlocker />
        <IdleTimer timeout={IdleTimeout} />
        <GuidedContainer data-area="guided-container" />

        <PopupMenu />
        <EmptyLeftAnalogStrip />
        <MonitorContentContainer>
          <UpperStrap>
            <StyledDiv {...dimension.sonar.roleInfoArea}>
              <RoleInfoArea monitor={DisplayConstants.UpperMonitor} />
            </StyledDiv>
            <ConfigControl
              elementFor={MonitorConfig.SONAR}
              config={currentConfig}
            >
              <StyledDiv {...dimension.sonar.workModeArea}>
                <WorkModeArea />
              </StyledDiv>
              <StyledDiv {...dimension.sonar.csraArea}>
                <CSRAArea currentConfig={MonitorConfig.SONAR} />
              </StyledDiv>
              <StyledDiv {...dimension.sonar.cursorInfoArea}>
                <CursorInfoArea currentConfig={MonitorConfig.SONAR} />
              </StyledDiv>
              <StyledDiv {...dimension.sonar.audioInfoArea}>
                <AudioInfoArea />
              </StyledDiv>
              <StyledDiv {...dimension.sonar.auxInfoArea}>
                <AuxInfoArea currentConfig={MonitorConfig.SONAR} />
              </StyledDiv>
            </ConfigControl>
            <ConfigControl
              elementFor={MonitorConfig.SISTEMA}
              config={currentConfig}
            >
              <StyledDiv {...dimension.sistema.csraArea}>
                <CSRAArea currentConfig={MonitorConfig.SISTEMA} />
              </StyledDiv>
              <StyledDiv {...dimension.sistema.upperSpareArea}></StyledDiv>
            </ConfigControl>
            <ConfigControl
              elementFor={MonitorConfig.TACTICO}
              config={currentConfig}
            >
              <StyledDiv {...dimension.tactico.csraArea}>
                <CSRAArea currentConfig={MonitorConfig.TACTICO} />
              </StyledDiv>
              <StyledDiv {...dimension.tactico.spareArea}>
                <SpareArea currentConfig={MonitorConfig.TACTICO} />
              </StyledDiv>
              <StyledDiv {...dimension.tactico.auxInfoArea}>
                <AuxInfoArea currentConfig={MonitorConfig.TACTICO} />
              </StyledDiv>
            </ConfigControl>
            <ConfigControl
              elementFor={MonitorConfig.LINK}
              config={currentConfig}
            >
              <StyledDiv {...dimension.link.upperSpareArea}></StyledDiv>
            </ConfigControl>
            <ConfigControl
              elementFor={MonitorConfig.OBT}
              config={currentConfig}
            >
              <StyledDiv {...dimension.link.upperSpareArea}></StyledDiv>
            </ConfigControl>
            <ConfigControl
              elementFor={MonitorConfig.WEAPON}
              config={currentConfig}
            >
              <StyledDiv {...dimension.weapon.csraArea}>
                <CSRAArea currentConfig={MonitorConfig.WEAPON} />
              </StyledDiv>
            </ConfigControl>
          </UpperStrap>
          <Body data-area="body">
            <MultimediaDialog />
            <GeneralDisplayArea
              ref={generalDisplayAreaRef}
              data-area="general-display-area"
            >
              <Map
                monitor={DisplayConstants.UpperMonitor}
                svgHeight={generalDisplayArea.height}
                svgWidth={generalDisplayArea.width}
              />
              <CommonInformationArea
                areaRef={generalDisplayAreaRef}
                data-area="common-info-area"
              />
              <ImageContainer monitor={DisplayConstants.UpperMonitor} />
              <TracksOnImage />
              <WindowDisplayManager
                monitor={DisplayConstants.UpperMonitor}
                area={DisplayConstants.GeneralDisplayArea}
                targetSystem={TargettedSystems.S80}
              />
            </GeneralDisplayArea>
            <RightStrip>
              <CloseControlArea>
                <EachCloseControlArea>
                  <hr />
                  <hr className="rightHr" />
                  <img
                    src={require('../../assets/images/primaryHookIcon.png')}
                  />
                  <PrimaryCloseControl monitor={S80Monitors.UPPER} isPrimary />
                </EachCloseControlArea>
                <EachCloseControlArea>
                  <hr />
                  <hr className="rightHr" />
                  <img
                    src={require('../../assets/images/secondaryHookIcon.png')}
                  />
                  <PrimaryCloseControl
                    monitor={S80Monitors.UPPER}
                    isSecondary
                  />
                </EachCloseControlArea>
              </CloseControlArea>
              <StyledDiv {...dimension.roleAssignmentArea} noPadding>
                <RoleAssignementArea data-area="role-assignment-area" />
              </StyledDiv>
              <DataEntryArea data-area="data-entry-area">
                <WindowDisplayManager
                  monitor={DisplayConstants.UpperMonitor}
                  area={DisplayConstants.DataEntryArea}
                  maxHeight={640}
                  targetSystem={TargettedSystems.S80}
                />
              </DataEntryArea>
            </RightStrip>
          </Body>
        </MonitorContentContainer>
        <EmptyRightAnalogStrip />
      </MonitorContext.Provider>
    </Container>
  );
}
